import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, CheckSquare, AlertTriangle, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, differenceInMinutes, addMinutes, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/hooks/useNotifications';

type NotificationType = 'reminder' | 'task' | 'lead_action' | 'notification';

interface UnifiedNotification {
  id: string;
  type: NotificationType;
  title: string;
  dueAt: Date;
  isOverdue: boolean;
  daysOverdue: number;
  leadId?: string;
  leadCompany?: string;
  priority: 'critical' | 'high' | 'normal';
}

export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [notifiedIds, setNotifiedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<string>('all');
  const [hasNewNotification, setHasNewNotification] = useState(false);

  // Use unified notifications hook (already has Realtime)
  const { notifications: systemNotifications, criticalCount: systemCriticalCount } = useNotifications();

  // Fetch upcoming reminders (next 24 hours, pending)
  const { data: upcomingReminders = [], refetch: refetchUpcoming } = useQuery({
    queryKey: ['upcoming-reminders'],
    queryFn: async () => {
      const now = new Date();
      const in24Hours = addMinutes(now, 24 * 60);
      
      const { data, error } = await supabase
        .from('reminders')
        .select(`
          *,
          lead:leads(id, company)
        `)
        .eq('status', 'pending')
        .gte('remind_at', now.toISOString())
        .lte('remind_at', in24Hours.toISOString())
        .order('remind_at', { ascending: true })
        .limit(20);
      
      if (error) throw error;
      return data;
    },
    refetchInterval: 180000, // Fallback 3 min
  });

  // Fetch overdue reminders
  const { data: overdueReminders = [], refetch: refetchOverdue } = useQuery({
    queryKey: ['overdue-reminders'],
    queryFn: async () => {
      const now = new Date();
      
      const { data, error } = await supabase
        .from('reminders')
        .select(`
          *,
          lead:leads(id, company)
        `)
        .eq('status', 'pending')
        .lt('remind_at', now.toISOString())
        .order('remind_at', { ascending: false })
        .limit(15);
      
      if (error) throw error;
      return data;
    },
    refetchInterval: 180000, // Fallback 3 min
  });

  // Fetch overdue tasks
  const { data: overdueTasks = [], refetch: refetchTasks } = useQuery({
    queryKey: ['overdue-tasks-bell'],
    queryFn: async () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('lead_tasks')
        .select(`
          *,
          lead:leads(id, company)
        `)
        .eq('done', false)
        .lt('due_date', today)
        .order('due_date', { ascending: true })
        .limit(10);
      
      if (error) throw error;
      return data;
    },
    refetchInterval: 180000, // Fallback 3 min
  });

  // Fetch leads with overdue next_action_date
  const { data: overdueLeadActions = [] } = useQuery({
    queryKey: ['overdue-lead-actions-bell'],
    queryFn: async () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('leads')
        .select('id, company, next_action_date')
        .lt('next_action_date', today)
        .not('status', 'in', '("won","lost")')
        .order('next_action_date', { ascending: true })
        .limit(10);
      
      if (error) throw error;
      return data;
    },
    refetchInterval: 180000, // Fallback 3 min
  });

  // Realtime subscription for reminders table
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('bell-reminders-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reminders',
        },
        () => {
          refetchUpcoming();
          refetchOverdue();
          setHasNewNotification(true);
          setTimeout(() => setHasNewNotification(false), 3000);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'lead_tasks',
        },
        () => {
          refetchTasks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, refetchUpcoming, refetchOverdue, refetchTasks]);

  // Show toast notifications for reminders coming up in 60 or 15 minutes
  useEffect(() => {
    const now = new Date();
    
    upcomingReminders.forEach((reminder: any) => {
      const remindAt = new Date(reminder.remind_at);
      const minutesUntil = differenceInMinutes(remindAt, now);
      
      if (minutesUntil <= 60 && minutesUntil > 55) {
        const key60 = `${reminder.id}-60`;
        if (!notifiedIds.has(key60)) {
          toast.info(`Rappel dans 1 heure`, {
            description: reminder.title,
            duration: 10000,
            action: reminder.lead ? {
              label: 'Voir',
              onClick: () => navigate(`/leads/${reminder.lead.id}`),
            } : undefined,
          });
          setNotifiedIds(prev => new Set(prev).add(key60));
        }
      }
      
      if (minutesUntil <= 15 && minutesUntil > 10) {
        const key15 = `${reminder.id}-15`;
        if (!notifiedIds.has(key15)) {
          toast.warning(`Rappel dans 15 minutes`, {
            description: reminder.title,
            duration: 15000,
            action: reminder.lead ? {
              label: 'Voir',
              onClick: () => navigate(`/leads/${reminder.lead.id}`),
            } : undefined,
          });
          setNotifiedIds(prev => new Set(prev).add(key15));
        }
      }
    });
  }, [upcomingReminders, notifiedIds, navigate]);

  // Build unified notification list
  const allNotifications: UnifiedNotification[] = [];

  // Add system notifications
  systemNotifications.filter(n => !n.read && !n.resolved).forEach((n) => {
    allNotifications.push({
      id: `notif-${n.id}`,
      type: 'notification',
      title: n.title,
      dueAt: new Date(n.created_at),
      isOverdue: false,
      daysOverdue: 0,
      leadId: n.lead?.id,
      leadCompany: n.lead?.company,
      priority: n.priority,
    });
  });

  // Add overdue reminders
  overdueReminders.forEach((r: any) => {
    const daysOverdue = differenceInDays(new Date(), new Date(r.remind_at));
    allNotifications.push({
      id: `reminder-${r.id}`,
      type: 'reminder',
      title: r.title,
      dueAt: new Date(r.remind_at),
      isOverdue: true,
      daysOverdue,
      leadId: r.lead?.id,
      leadCompany: r.lead?.company,
      priority: daysOverdue > 1 ? 'critical' : 'high',
    });
  });

  // Add upcoming reminders
  upcomingReminders.forEach((r: any) => {
    const minutesUntil = differenceInMinutes(new Date(r.remind_at), new Date());
    allNotifications.push({
      id: `reminder-${r.id}`,
      type: 'reminder',
      title: r.title,
      dueAt: new Date(r.remind_at),
      isOverdue: false,
      daysOverdue: 0,
      leadId: r.lead?.id,
      leadCompany: r.lead?.company,
      priority: minutesUntil <= 15 ? 'high' : 'normal',
    });
  });

  // Add overdue tasks
  overdueTasks.forEach((t: any) => {
    const daysOverdue = differenceInDays(new Date(), new Date(t.due_date));
    allNotifications.push({
      id: `task-${t.id}`,
      type: 'task',
      title: t.title,
      dueAt: new Date(t.due_date),
      isOverdue: true,
      daysOverdue,
      leadId: t.lead?.id,
      leadCompany: t.lead?.company,
      priority: daysOverdue > 2 ? 'critical' : 'high',
    });
  });

  // Add overdue lead actions
  overdueLeadActions.forEach((l: any) => {
    const daysOverdue = differenceInDays(new Date(), new Date(l.next_action_date));
    allNotifications.push({
      id: `lead-${l.id}`,
      type: 'lead_action',
      title: `Relancer ${l.company}`,
      dueAt: new Date(l.next_action_date),
      isOverdue: true,
      daysOverdue,
      leadId: l.id,
      leadCompany: l.company,
      priority: daysOverdue > 3 ? 'critical' : daysOverdue > 1 ? 'high' : 'normal',
    });
  });

  // Sort by priority and overdue status
  const priorityOrder = { critical: 0, high: 1, normal: 2 };
  allNotifications.sort((a, b) => {
    if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    return b.daysOverdue - a.daysOverdue;
  });

  // Filter by tab
  const filteredNotifications = activeTab === 'all' 
    ? allNotifications 
    : allNotifications.filter(n => n.type === activeTab);

  const criticalCount = allNotifications.filter(n => n.priority === 'critical').length;
  const overdueCount = allNotifications.filter(n => n.isOverdue).length;
  const unreadCount = criticalCount > 0 ? criticalCount : overdueCount;

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case 'reminder': return <Bell className="h-3.5 w-3.5" />;
      case 'task': return <CheckSquare className="h-3.5 w-3.5" />;
      case 'lead_action': return <Calendar className="h-3.5 w-3.5" />;
      case 'notification': return <Bell className="h-3.5 w-3.5" />;
    }
  };

  const handleNotificationClick = (notification: UnifiedNotification) => {
    if (notification.leadId) {
      navigate(`/leads/${notification.leadId}`);
    } else if (notification.type === 'reminder') {
      navigate('/planning/day');
    } else {
      navigate('/notifications');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant={criticalCount > 0 ? 'destructive' : 'default'}
              className={cn(
                "absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs",
                criticalCount > 0 && "animate-pulse",
                hasNewNotification && "animate-bounce"
              )}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            Notifications
            {criticalCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {criticalCount} urgent{criticalCount > 1 ? 's' : ''}
              </Badge>
            )}
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-auto p-0 text-xs text-primary"
            onClick={() => navigate('/notifications')}
          >
            Tout voir
          </Button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Tabs for filtering */}
        <div className="px-2 py-1">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4 h-8">
              <TabsTrigger value="all" className="text-xs">Tout</TabsTrigger>
              <TabsTrigger value="reminder" className="text-xs">Rappels</TabsTrigger>
              <TabsTrigger value="task" className="text-xs">Tâches</TabsTrigger>
              <TabsTrigger value="lead_action" className="text-xs">Leads</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        <ScrollArea className="h-[320px]">
          {filteredNotifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Aucune notification
            </div>
          ) : (
            filteredNotifications.slice(0, 15).map((notification) => (
              <DropdownMenuItem 
                key={notification.id}
                className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-2 w-full">
                  <div className={cn(
                    'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
                    notification.priority === 'critical' ? 'bg-destructive animate-pulse' : 
                    notification.priority === 'high' ? 'bg-orange-500' :
                    notification.isOverdue ? 'bg-yellow-500' : 'bg-primary'
                  )} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-muted-foreground">{getTypeIcon(notification.type)}</span>
                      <p className="text-sm font-medium truncate">{notification.title}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {notification.isOverdue ? (
                        <span className="text-destructive font-medium">
                          {notification.daysOverdue === 0 ? "Aujourd'hui" : 
                           notification.daysOverdue === 1 ? 'Hier' : 
                           `Il y a ${notification.daysOverdue} jours`}
                        </span>
                      ) : (
                        <span>
                          {differenceInMinutes(notification.dueAt, new Date()) <= 60 
                            ? `Dans ${differenceInMinutes(notification.dueAt, new Date())} min`
                            : format(notification.dueAt, 'HH:mm', { locale: fr })}
                        </span>
                      )}
                      {notification.leadCompany && (
                        <span className="text-muted-foreground"> • {notification.leadCompany}</span>
                      )}
                    </p>
                  </div>
                  {notification.priority === 'critical' && (
                    <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0" />
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
        
        {filteredNotifications.length > 15 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-center text-sm text-primary cursor-pointer justify-center"
              onClick={() => navigate('/notifications')}
            >
              Voir les {filteredNotifications.length - 15} autres...
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
